import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { SearchInput } from '@/components/ui/SearchInput';
import { EmptyState } from '@/components/ui/EmptyState';
import { FitText } from '@/components/ui/FitText';
import { useCreateReturn, useReturnsByTransaction } from '@/hooks/useReturns';
import { useTransaction } from '@/hooks/useTransactions';
import { useAuth } from '@/hooks/useAuth';
import { formatDate, formatIDR, formatNumber } from '@/lib/format';
import { listTransactionsByEvent } from '@/lib/api/transactions';
import type { AppTransaction, RefundMethod } from '@/types/domain';
import { useQuery } from '@tanstack/react-query';
import { useActiveEvent } from '@/hooks/useActiveEvent';

export interface ReturnFormProps {
  open: boolean;
  onClose: () => void;
  prefillTransactionId?: string | null;
}

interface ItemReturn {
  /**
   * The line's key. An invoice can carry the same product twice from two lots,
   * and those are separate lines with separate returned_qty — keying the form
   * by product would let one be over-returned while the other went untouched.
   */
  lineKey: string;
  productId: string;
  allocationId?: string;
  name: string;
  lotNumber?: string;
  expiryDate?: number;
  unit?: string;
  maxQty: number;
  returnQty: number;
  unitPrice: number;
}

export function ReturnForm({ open, onClose, prefillTransactionId }: ReturnFormProps) {
  const { user } = useAuth();
  const { activeEventId } = useActiveEvent();
  const createReturn = useCreateReturn();

  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedTx, setSelectedTx] = useState<AppTransaction | null>(null);
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('original');
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});

  // Search for transactions
  const { data: searchResults } = useQuery({
    queryKey: ['returns', 'search', activeEventId, invoiceSearch],
    queryFn: async () => {
      if (!activeEventId || !invoiceSearch) return [];
      const all = await listTransactionsByEvent(activeEventId);
      const q = invoiceSearch.toLowerCase();
      return all
        .filter(
          (t) =>
            t.invoiceNumber.toLowerCase().includes(q) ||
            (t.customerSnapshot?.name?.toLowerCase().includes(q) ?? false),
        )
        .slice(0, 20);
    },
    enabled: !!activeEventId && invoiceSearch.length > 0,
  });

  // Prefill if transaction ID provided
  const { data: prefillTx } = useTransaction(prefillTransactionId ?? null);
  useEffect(() => {
    if (prefillTransactionId && prefillTx) {
      setSelectedTx(prefillTx);
      setInvoiceSearch(prefillTx.invoiceNumber);
    }
  }, [prefillTransactionId, prefillTx]);

  // Build returnable items
  const returnable: ItemReturn[] = useMemo(() => {
    if (!selectedTx) return [];
    return selectedTx.items
      .filter((it) => it.qty - (it.returnedQty ?? 0) > 0)
      .map((it) => {
        const lineKey = it.allocationId ?? it.productId;
        return {
          lineKey,
          productId: it.productId,
          allocationId: it.allocationId,
          name: it.name,
          lotNumber: it.lotNumber,
          expiryDate: it.expiryDate,
          unit: it.unit,
          maxQty: it.qty - (it.returnedQty ?? 0),
          returnQty: returnQty[lineKey] ?? 0,
          unitPrice: it.price,
        };
      });
  }, [selectedTx, returnQty]);

  // What create_return() will actually refund. Line prices are list prices —
  // the invoice's discount and tax sit on the transaction — so the refund is
  // the returned lines' share of grandTotal, counted from the invoice's whole
  // returned value and net of what earlier returns already gave back. Mirrors
  // the function exactly so the number in the button is the number recorded.
  const { data: priorReturns } = useReturnsByTransaction(selectedTx?.id ?? null);

  const totalRefund = useMemo(() => {
    if (!selectedTx || selectedTx.subtotal <= 0) return 0;
    const alreadyReturnedValue = selectedTx.items.reduce(
      (sum, it) => sum + (it.returnedQty ?? 0) * it.price,
      0,
    );
    const nowReturnedValue = returnable.reduce(
      (sum, it) => sum + it.returnQty * it.unitPrice,
      0,
    );
    const alreadyRefunded = (priorReturns ?? []).reduce((sum, r) => sum + r.refundAmount, 0);
    const share = Math.round(
      ((alreadyReturnedValue + nowReturnedValue) * selectedTx.grandTotal) / selectedTx.subtotal,
    );
    return Math.max(0, share - alreadyRefunded);
  }, [selectedTx, returnable, priorReturns]);

  const hasReturns = returnable.some((it) => it.returnQty > 0);

  const handleClose = () => {
    setSelectedTx(null);
    setReturnQty({});
    setInvoiceSearch('');
    setRefundMethod('original');
    onClose();
  };

  const handleSubmit = async () => {
    if (!user || !selectedTx || !hasReturns) return;
    // Resolved back through `returnable` so each entry carries its allocation,
    // which is the shelf the stock goes back onto.
    const items = returnable
      .filter((it) => it.returnQty > 0)
      .map((it) => ({
        productId: it.productId,
        allocationId: it.allocationId,
        qty: it.returnQty,
      }));
    if (items.length === 0) return;

    await createReturn.mutateAsync({
      transactionId: selectedTx.id,
      items,
      refundMethod,
      reason: undefined,
    });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New return"
      description="Kembalikan sebagian atau seluruh item dari invoice."
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={createReturn.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!hasReturns}
            loading={createReturn.isPending}
          >
            Process return ({formatIDR(totalRefund)})
          </Button>
        </>
      }
    >
      {!selectedTx ? (
        <div className="space-y-3">
          <SearchInput
            value={invoiceSearch}
            onChange={setInvoiceSearch}
            placeholder="Cari invoice (nomor / nama customer)…"
          />
          {!invoiceSearch ? (
            <EmptyState
              title="Cari invoice dulu"
              description="Ketik nomor invoice atau nama customer untuk mulai."
            />
          ) : searchResults && searchResults.length === 0 ? (
            <EmptyState
              title="Tidak ada invoice"
              description={`Tidak ada invoice cocok dengan "${invoiceSearch}" di event ini.`}
            />
          ) : (
            <div className="border border-line rounded-lg divide-y divide-line max-h-80 overflow-y-auto">
              {searchResults?.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setSelectedTx(t);
                    setInvoiceSearch(t.invoiceNumber);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-canvas/50 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-body font-medium text-ink font-mono">
                      {t.invoiceNumber}
                    </p>
                    <p className="text-caption text-ink-subtle">
                      {t.customerSnapshot?.name ?? 'Walk-in'} • {t.items.length} item •{' '}
                      {formatIDR(t.grandTotal)}
                    </p>
                  </div>
                  <Chip
                    variant={
                      t.paymentStatus === 'paid'
                        ? 'success'
                        : t.paymentStatus === 'partially_returned'
                          ? 'info'
                          : 'warning'
                    }
                  >
                    {t.paymentStatus.replace('_', ' ')}
                  </Chip>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {/* Invoice summary */}
          <div className="p-4 rounded bg-canvas border border-line">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-overline uppercase text-ink-subtle">Invoice</p>
                <p className="text-body font-medium text-ink font-mono">
                  {selectedTx.invoiceNumber}
                </p>
                <p className="text-caption text-ink-subtle">
                  {selectedTx.customerSnapshot?.name ?? 'Walk-in'} •{' '}
                  {formatIDR(selectedTx.grandTotal)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedTx(null);
                  setReturnQty({});
                }}
              >
                Ganti invoice
              </Button>
            </div>
          </div>

          {/* Items */}
          <div>
            <p className="text-small font-medium text-ink mb-2">Pilih item yang dikembalikan</p>
            <div className="border border-line rounded-lg divide-y divide-line">
              {returnable.map((it) => (
                <div
                  key={it.lineKey}
                  className="px-4 py-3 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-body font-medium text-ink truncate">{it.name}</p>
                    <p className="text-caption text-ink-subtle">
                      {formatIDR(it.unitPrice)} • Max return: {formatNumber(it.maxQty)}
                      {it.unit ? ` ${it.unit}` : ''}
                    </p>
                    {(it.lotNumber || it.expiryDate) && (
                      <p className="text-caption text-ink-subtle">
                        {it.lotNumber && <>Lot {it.lotNumber}</>}
                        {it.lotNumber && it.expiryDate && ' • '}
                        {it.expiryDate && <>ED {formatDate(it.expiryDate)}</>}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setReturnQty((q) => ({
                          ...q,
                          [it.lineKey]: Math.max(0, (q[it.lineKey] ?? 0) - 1),
                        }))
                      }
                      className="w-8 h-8 rounded border border-line hover:bg-line/30 transition-colors"
                      disabled={it.returnQty === 0}
                    >
                      −
                    </button>
                    <span className="w-10 text-center text-body font-medium tabular-nums">
                      {it.returnQty}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setReturnQty((q) => ({
                          ...q,
                          [it.lineKey]: Math.min(it.maxQty, (q[it.lineKey] ?? 0) + 1),
                        }))
                      }
                      className="w-8 h-8 rounded border border-line hover:bg-line/30 transition-colors"
                      disabled={it.returnQty >= it.maxQty}
                    >
                      +
                    </button>
                    <div className="w-24 text-right text-small font-medium tabular-nums text-ink">
                      {formatIDR(it.returnQty * it.unitPrice)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Refund method */}
          <div>
            <p className="text-small font-medium text-ink mb-2">Refund method</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(
                [
                  { value: 'original', label: 'Original' },
                  { value: 'cash', label: 'Cash' },
                  { value: 'qris', label: 'QRIS' },
                  { value: 'transfer', label: 'Transfer' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRefundMethod(opt.value)}
                  className={`px-3 py-2 rounded text-small font-medium border transition-colors ${
                    refundMethod === opt.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-line text-ink-subtle hover:border-ink/30'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="flex items-center justify-between p-4 rounded bg-canvas border border-line">
            <div>
              <p className="text-body font-medium text-ink">Total refund</p>
              {/* The per-item figures above are list prices. When the invoice
                  carried a discount or tax, the refund is that basket's share
                  of what was actually charged, so the two will not match. */}
              {(selectedTx.discountAmount > 0 || selectedTx.taxAmount > 0) && (
                <p className="text-small text-ink-subtle mt-1">
                  Sesuai porsi yang dibayar (invoice ada
                  {selectedTx.discountAmount > 0 ? ' diskon' : ''}
                  {selectedTx.discountAmount > 0 && selectedTx.taxAmount > 0 ? ' &' : ''}
                  {selectedTx.taxAmount > 0 ? ' pajak' : ''})
                </p>
              )}
            </div>
            <FitText className="text-metric font-display font-semibold text-ink tabular-nums">
              {formatIDR(totalRefund)}
            </FitText>
          </div>
        </div>
      )}
    </Modal>
  );
}
